import { GridItem, Text, GhostButton } from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { css } from "@emotion/css";
import { useField } from "formik";
import { useContext, useState } from "react";
import { Trans } from "react-i18next";

import { CredentialModal } from "./CredentialModal";
import { CredentialsList } from "./CredentialsList";
import { Credential } from "./type";
import { ApplicationContext } from "../../Application.context";

type CredentialsCheckField = components["schemas"]["CredentialsCheckField"];

const sortHighestFirst = (credentials: Credential[]) => {
  return credentials.sort((a: Credential, b: Credential) => {
    return Number(b?.highest_level) - Number(a?.highest_level);
  });
};

const sortByDate = (credentials: Credential[]) => {
  return credentials
    .map((credential, i) => ({ ...credential, index: i }))
    .sort((credential: Credential) => {
      return credential.date_of_certification ? -1 : 1;
    });
};

export function CredentialInputField({
  field: config,
}: {
  field: CredentialsCheckField;
}) {
  const { saveData } = useContext(ApplicationContext);
  const [field, , helpers] = useField<CredentialsCheckField["value"]>(
    config.name
  );
  const [credentials, setCredentials] = useState<Credential[]>(
    field.value.map((a, index) => ({ ...a, index }))
  );

  const sortCredentials = (credentials: Credential[]) => {
    let sortedCredentials;

    if (config.require_highest_level) {
      sortedCredentials = sortHighestFirst(credentials);
    }

    sortedCredentials = sortByDate(credentials);

    return sortedCredentials.map((a, index) => ({ ...a, index }));
  };

  const handleSaveCredential = (credential: Credential) => {
    let newCredentials = [...credentials];
    newCredentials[credential.index] = credential;
    newCredentials = sortCredentials(newCredentials);
    saveEntries(newCredentials);
  };

  const handleDeleteCredential = (index: number) => {
    let newCredentials = [...credentials].filter(
      (credential) => index !== credential.index
    );
    newCredentials = sortCredentials(newCredentials);
    saveEntries(newCredentials);
  };

  const saveEntries = async (entries: Credential[]) => {
    setCredentials(entries);
    await helpers.setValue(entries); // await required for race condition
    await saveData({ skipValidation: true, hasMultipleEntries: true });
  };

  const hasHighestCredential = credentials.find(
    ({ highest_level }) => highest_level
  );

  return (
    <>
      <GridItem>
        <Text as="h2" style="form/section-heading">
          <Trans>My credentials</Trans>
        </Text>
      </GridItem>

      {credentials.length > 0 && (
        <CredentialsList
          testID="credential-input-field-credentials-list"
          credentials={credentials}
          onSave={handleSaveCredential}
          onDelete={handleDeleteCredential}
          requireHighestLevel={config.require_highest_level}
        />
      )}

      <GridItem className={css({ display: "block" })}>
        <CredentialModal
          index={credentials.length}
          allowHighest={config.require_highest_level && !hasHighestCredential}
          onSave={handleSaveCredential}
          trigger={
            <GhostButton
              testID="credential-input-field-credential-modal-button"
              leftIcon="add"
              size="medium"
              fill="background-base"
              border="border-strong"
            >
              <Trans>Add credential</Trans>
            </GhostButton>
          }
        />
      </GridItem>
    </>
  );
}
